import { Theme } from '@emotion/react'
import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { StyledButton } from '../../../../buttons/Button/Button.styled'
import { colors } from '../../../../__styling/settings/colors'
import {
    breakpoint,
    breakpointMax,
} from '../../../../__styling/style-functions'
import { calcRem } from '../../../../__styling/style-functions/calc-rem'
import { fluidTypography } from '../../../../__styling/style-mixins'
import { themedValue } from '../../../../__styling/themed-value'

const SubscribeWithGoogle = require('./SubscribeWithGoogle.svg')

export type PackageTheme = 'default' | 'red' | 'yellow' | '190year' | 'abtest'
export interface PackageThemeProps {
    packageTheme: PackageTheme
    isRegional?: boolean
    useBreachDealStyle?: boolean
    useAbTestStyle?: boolean
}

export const StyledSubscriptionPackage = styled('div')<PackageThemeProps>(
    ({ theme, packageTheme, isRegional, useBreachDealStyle }) => ({
        flexBasis: calcRem(278),
        flexShrink: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        padding: calcRem(theme.margins.md, 24),
        margin: calcRem(theme.margins.md),
        borderRadius: 6,
        fontFamily: theme.fonts.subscriber.text,
        background:
            packageTheme === '190year'
                ? tokens.thewest.colors.palette.white
                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.5) 0%, rgba(191, 205, 216, 0.5) 100%), #FFF;',
        color:
            packageTheme === '190year'
                ? tokens.thewest.colors.palette.westblue
                : themedValue(theme, {
                      thewest: colors.thewest.westblue,
                      perthnow: colors.perthnow.pinkThulite,
                      sevennews: colors.sevennews.red,
                  }),
        boxShadow:
            packageTheme === '190year'
                ? '0 2px 21px 0 rgba(47, 48, 104, 0.4)'
                : isRegional && useBreachDealStyle
                ? '0 2px 21px 0 rgba(47, 48, 104, 0.4)'
                : '0 2px 21px 0 rgba(0,0,0,0.5)',
        [breakpointMax('xxs')]: {
            margin: calcRem(theme.margins.md, 0),
            flexBasis: calcRem(250),
        },
    }),
)

export const StyledSubscriptionPackage2022 = styled(
    StyledSubscriptionPackage,
)<PackageThemeProps>(({ theme, packageTheme, useAbTestStyle }) => ({
    flexBasis:
        packageTheme === '190year'
            ? calcRem(320)
            : useAbTestStyle
            ? '46%'
            : calcRem(312),
    margin:
        packageTheme === '190year' ? calcRem(0, 20, 20) : calcRem(0, 12, 18, 0),
    padding: calcRem(24),
    background: theme.colors.background.primary || 'white',
    boxShadow:
        packageTheme === '190year'
            ? '0 2px 21px 0 rgba(47, 48, 104, 0.4)'
            : undefined,

    minHeight: useAbTestStyle ? calcRem(722) : 'auto',
    '&:nth-child(2n)': {
        marginRight: useAbTestStyle ? 0 : calcRem(12),
    },

    [breakpointMax('md')]: {
        flexBasis: calcRem(312),
    },
    [breakpointMax('xxs')]: {
        marginRight: 0,
        flexGrow: 1,
    },
}))

interface StyledPackageImageProps {
    isRegional?: boolean
    isEditionsModal?: boolean
}

export const StyledPackageImage = styled('img')<StyledPackageImageProps>(
    ({ isRegional, isEditionsModal }) => ({
        width: isEditionsModal ? '75%' : '95%',
        display: 'block',
        margin: '0 auto',
        marginTop: isRegional && !isEditionsModal ? calcRem(25) : 0,
        transform: 'scale(1.1)',
        height: '180px',
        objectFit: 'contain',
    }),
)

export const StyledMiniPackageImage = styled(StyledPackageImage)({
    height: 'auto',
    transform: 'unset',
})

export const StyledSubscriptionPackageHeading = styled('h4')<PackageThemeProps>(
    ({ theme }) => ({
        fontSize: calcRem(24),
        fontWeight: 700,
        lineHeight: 1.167,
        margin: calcRem(theme.margins.md, 0, theme.margins.sm, 0),
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
        }),

        flex: '0 0 auto', // fixes ie11 column wrap bug
    }),
)

export const StyledSubscriptionPackageHeading2022 = styled('h4')(
    ({ theme }) => ({
        fontFamily: theme.fonts.serif,
        fontSize: calcRem(22),
        lineHeight: calcRem(24),
        margin: calcRem(theme.margins.md, 0, theme.margins.sm, 0),
        color: themedValue(theme, {
            thewest: colors.black,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
        }),

        flex: '0 0 auto', // fixes ie11 column wrap bug
    }),
)

export const StyledSubscriptionPackageOriginalPrice = styled(
    'h4',
)<PackageThemeProps>(({ theme }) => ({
    fontSize: calcRem(16),
    fontWeight: 700,
    lineHeight: 1.167,
    textDecoration: 'line-through',
    textTransform: 'uppercase',
    margin: 0,
    color: themedValue(theme, {
        thewest: colors.thewest.greyGorilla,
        perthnow: colors.perthnow.greyBasalt90,
        sevennews: colors.sevennews.charade,
    }),

    flex: '0 0 auto', // fixes ie11 column wrap bug

    [breakpointMax('xs')]: {
        fontSize: calcRem(16),
    },
}))

export const StyledSubscriptionPackageOriginalPrice2022 = styled(
    StyledSubscriptionPackageOriginalPrice,
)(({ theme, packageTheme, isRegional, useBreachDealStyle }) => ({
    fontSize: calcRem(16),
    fontFamily: theme.fonts.sansSerifCond,
    color:
        packageTheme === '190year' && isRegional
            ? tokens.thewest.colors.palette.westblue
            : isRegional && useBreachDealStyle
            ? tokens.thewest.colors.palette.westblue
            : theme.sectionValues.primaryColor,
    fontFeatureSettings: "'pnum' on, 'lnum' on",
}))

const themedPriceFluid = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(375, 1000, 24, 28),
        perthnow: fluidTypography(375, 1020, 24, 30),
        thewest: fluidTypography(375, 1220, 24, 24),
    })

export const StyledSubscriptionPackagePrice = styled('p')<PackageThemeProps>(
    ({ theme, packageTheme }) => ({
        ...themedPriceFluid(theme),
        flex: '0 0 auto', // fixes ie11 column wrap bug
        fontWeight: 500,
        lineHeight: 1.167,
        margin: 0,
        color: theme.sectionValues.primaryColor,
    }),
)

export const StyledSubscriptionPackagePrice2022 = styled(
    StyledSubscriptionPackagePrice,
)(({ theme, packageTheme, isRegional, useBreachDealStyle }) => ({
    flex: '0 0 auto', // fixes ie11 column wrap bug
    fontSize: calcRem(32),
    fontWeight: 700,
    fontFamily: theme.fonts.sansSerifCond,
    fontFeatureSettings: "'pnum' on, 'lnum' on",
    textTransform: 'uppercase',
    color:
        packageTheme === '190year' && isRegional
            ? tokens.thewest.colors.palette.westblue
            : isRegional && useBreachDealStyle
            ? tokens.thewest.colors.palette.westblue
            : theme.sectionValues.primaryColor,
}))

export const StyledSubscriptionPackagePriceQualifier = styled(
    'p',
)<PackageThemeProps>(({ theme, packageTheme }) => ({
    fontSize: calcRem(14),
    flex: '0 0 auto', // fixes ie11 column wrap bug
    fontWeight: 700,
    lineHeight: 1.167,
    margin: 0,
    color:
        packageTheme === 'red'
            ? colors.thewest.ladybird
            : packageTheme === 'yellow'
            ? colors.thewest.sunshineYellow
            : packageTheme == '190year'
            ? tokens.thewest.colors.palette.anniversary190YearPrimary
            : packageTheme == 'abtest'
            ? tokens.thewest.colors.palette.anniversary190YearPrimary
            : undefined,
}))

export const StyledSubscriptionPackagePriceDivider2022 = styled(
    'hr',
)<PackageThemeProps>(
    ({ theme, packageTheme, isRegional, useBreachDealStyle }) => ({
        width: '100%',
        maxWidth: calcRem(53),
        height: calcRem(4),
        backgroundColor:
            packageTheme == '190year'
                ? tokens.thewest.colors.palette.anniversary190YearPrimary
                : isRegional && useBreachDealStyle
                ? tokens.thewest.colors.palette.westblue
                : theme.sectionValues.primaryColor,
        marginLeft: 0,
        borderRadius: calcRem(4),
        border: 'none',
        marginTop: calcRem(6),
        marginBottom: calcRem(16),
    }),
)

export const StyledSubscriptionPackageActions = styled('div')({
    width: '100%',
    alignSelf: 'center',
    textAlign: 'center',
})

export const StyledSubscriptionPackageButton = styled(
    StyledButton, //target styledbutton
)<PackageThemeProps>(
    ({ theme, packageTheme, isRegional, useBreachDealStyle }) => ({
        margin: calcRem(theme.margins.md, 0, 0),
        width: calcRem(200),
        height: calcRem(40),
        fontSize: calcRem(16),

        [breakpoint('xxs')]: {
            fontSize: calcRem(18),
            width: calcRem(230),
        },

        color:
            packageTheme === '190year'
                ? tokens.thewest.colors.palette.anniversary190YearPrimary
                : undefined,

        backgroundColor:
            packageTheme === 'red'
                ? colors.thewest.ladybird
                : packageTheme === 'yellow'
                ? colors.thewest.sunshineYellow
                : packageTheme === '190year'
                ? tokens.thewest.colors.palette.anniversary190YearTeal
                : isRegional && useBreachDealStyle
                ? tokens.thewest.colors.palette.westblue
                : undefined,

        background:
            useBreachDealStyle && packageTheme === 'abtest'
                ? tokens.thewest.colors.palette.newWestGradient.purpleBlue2
                : packageTheme === 'red'
                ? colors.thewest.ladybird
                : packageTheme === 'yellow'
                ? colors.thewest.sunshineYellow
                : packageTheme === '190year'
                ? tokens.thewest.colors.palette.anniversary190YearTeal
                : isRegional && useBreachDealStyle
                ? tokens.thewest.colors.palette.westblue
                : undefined,
    }),
)

export const StyledSubscriptionPackageButton2022 = styled(
    StyledSubscriptionPackageButton,
)<PackageThemeProps>({
    fontWeight: 500,
    width: '100%',
    textTransform: 'none',
    transition: '0s background ease',
    [breakpoint('xxs')]: { width: '100%' },
})

export const StyledSubscriptionCancellationMessage = styled(
    'p',
)<PackageThemeProps>(({ theme }) => ({
    fontSize: calcRem(12),
    flex: '0 0 auto', // fixes ie11 column wrap bug
    fontWeight: 400,
    lineHeight: 1.167,
    margin: 'auto 0 0',
    color: themedValue(theme, {
        thewest: colors.thewest.greyRhino,
        perthnow: colors.perthnow.greyBasalt90,
        sevennews: colors.sevennews.charade,
    }),
    paddingTop: calcRem(theme.margins.sm),
}))

export const Ribbon = styled('p')<PackageThemeProps>(
    ({ theme, packageTheme }) => ({
        position: 'absolute',
        top: 0,
        right: 0,
        background:
            packageTheme === 'red'
                ? colors.thewest.ladybird
                : packageTheme === 'yellow'
                ? colors.thewest.sunshineYellow
                : packageTheme === '190year'
                ? tokens.thewest.colors.palette.anniversary190YearTeal
                : packageTheme === 'abtest'
                ? tokens.thewest.colors.palette.newWestGradient.purpleBlue
                : 'linear-gradient(45deg, #aa01e1 0%,#f400ab 100%)',
        transform: 'rotate(45deg) translate(15%,-100%)',
        height: 80,
        width: 160,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        textTransform: 'uppercase',
        fontSize: 10,
        fontWeight: 700,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.5)',

        span: {
            color:
                packageTheme === '190year'
                    ? tokens.thewest.colors.palette.anniversary190YearPrimary
                    : colors.white,
            display: 'block',
            width: 50,
            textAlign: 'center',
            lineHeight: 1,
            marginBottom: theme.margins.xs,
        },

        svg: {
            position: 'absolute',
            left: '50%',
            transform: 'translate(-50%, calc(-100% - 5px))',
        },

        '&.cornerDesignTag': {
            height: 94,
            width: 234,
            fontSize: packageTheme === '190year' ? 20 : 24,
            span: {
                marginBottom: 10,
                width: packageTheme === '190year' ? 78 : 50,
            },
            svg: {
                display: 'none',
            },
        },

        '&.newDesignTag': {
            position: 'unset',
            transform: 'none',
            height: 'auto',
            width: '90px',
            padding: '10px',
            paddingRight: '25px',
            marginTop: '20px',
            marginBottom: '-5px',
            borderRadius: '30px',

            span: {
                position: 'relative',
                margin: 0,
                fontSize: '0.65rem',

                svg: {
                    left: 'unset',
                    right: '-16px',
                    width: '12px',
                    transform: 'translateY(-3px)',
                },
            },
        },
    }),
)

export const StyledSubscriptionPackageBenefit = styled.div(({ theme }) => ({
    fontFamily: theme.fonts.serif,
    fontSize: calcRem(16),
    color: 'black',
    display: 'flex',
    margin: calcRem(6, 0),
    alignItems: 'center',
    textAlign: 'start',

    '& > :first-child': {
        flex: '0 0 ' + calcRem(20),
    },
    '& > :last-child': {
        flex: '1 1 auto',
        marginLeft: calcRem(8),
    },
}))

export const StyledSubscribeWithGoogleButton = styled('button')(
    ({ theme }) => ({
        marginTop: theme.margins.sm,
        cursor: 'pointer',
        maxWidth: '100%',
        borderRadius: '4px',
    }),
)

export const StyledSubscribeWithGoogleButton2022 = styled(
    StyledSubscribeWithGoogleButton,
)({
    width: '100%',
    padding: 0,
    borderRadius: calcRem(2),
    border: `1px solid ${colors.thewest.greyKoala}`,
    boxShadow: 'none',
    '&::after': {
        content: "''",
        position: 'relative',
        display: 'inline-block',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url('${SubscribeWithGoogle}')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
    },
    '&:hover': {
        backgroundColor: '#f8f8f0',
    },
})
